import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from 'react'
import { Check, ClipboardPaste, ExternalLink, FileUp, ImagePlus, Link2, LoaderCircle, Search, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, type ArtworkSearchCandidate, type ArtworkSearchResponse, type ArtworkSelection, type BroadcasterArtworkSuggestionResponse } from '../lib/api'
import { ModalPortal } from './ModalPortal'

interface Props {
  eventId: number
  initialQuery: string
  hasManualArtwork: boolean
  externalError?: string
  onSelect: (selection: ArtworkSelection) => Promise<void>
  onUpload: (file: File) => Promise<void>
  onRemove?: () => Promise<void>
  onClose: () => void
}

type Tab = 'search' | 'paste' | 'url'

export function ArtworkWizard({ eventId, initialQuery, hasManualArtwork, externalError,
  onSelect, onUpload, onRemove, onClose }: Props) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('search')
  const [query, setQuery] = useState(initialQuery)
  const [recommended, setRecommended] = useState<ArtworkSearchCandidate | null>(null)
  const [results, setResults] = useState<ArtworkSearchCandidate[]>([])
  const [selected, setSelected] = useState<ArtworkSearchCandidate | null>(null)
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [actionError, setActionError] = useState('')
  const [url, setUrl] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [filePreview, setFilePreview] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  const runSearch = async (value = query) => {
    const normalized = value.replace(/\s+/g, ' ').trim()
    if (normalized.length < 2) return
    setSearching(true)
    setSearchError('')
    setSelected(null)
    const [openverse, sender] = await Promise.allSettled([
      api<ArtworkSearchResponse>(`/api/v1/metadata/artwork-search?query=${encodeURIComponent(normalized)}`),
      api<BroadcasterArtworkSuggestionResponse>(`/api/v1/metadata/artwork-search/broadcaster?eventId=${eventId}`),
    ])
    if (sender.status === 'fulfilled') setRecommended(sender.value.result)
    else setRecommended(null)
    if (openverse.status === 'fulfilled') {
      setResults(openverse.value.results)
      setQuery(openverse.value.query)
    } else {
      setResults([])
      setSearchError(openverse.reason instanceof Error ? openverse.reason.message : t('newMovies.artworkSearchFailed'))
    }
    setSearching(false)
  }

  useEffect(() => { void runSearch(initialQuery) }, [eventId, initialQuery])
  useEffect(() => {
    if (!file) { setFilePreview(''); return }
    const objectUrl = URL.createObjectURL(file)
    setFilePreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  const save = async (action: () => Promise<void>) => {
    setSaving(true)
    setActionError('')
    try {
      await action()
      onClose()
    } catch (error) {
      setActionError(error instanceof Error ? error.message : t('newMovies.artworkSaveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const acceptFile = (candidate: File | null) => {
    if (!candidate) return
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(candidate.type)) {
      setActionError(t('newMovies.artworkUnsupportedFormat'))
      return
    }
    setActionError('')
    setFile(candidate)
    setTab('paste')
  }

  const readClipboard = async () => {
    setActionError('')
    try {
      const clipboard = navigator.clipboard as Clipboard & { read?: () => Promise<ClipboardItem[]> }
      if (!clipboard.read) throw new Error(t('newMovies.clipboardUnavailable'))
      const items = await clipboard.read()
      for (const item of items) {
        const imageType = item.types.find((type) => type.startsWith('image/'))
        if (imageType) {
          const blob = await item.getType(imageType)
          acceptFile(new File([blob], 'zwischenablage.' + extensionFor(imageType), { type: imageType }))
          return
        }
      }
      const text = await navigator.clipboard.readText()
      if (isHttpUrl(text)) {
        setUrl(text.trim()); setTab('url'); return
      }
      throw new Error(t('newMovies.clipboardNoImage'))
    } catch (error) {
      setActionError(error instanceof Error ? error.message : t('newMovies.clipboardUnavailable'))
    }
  }

  const handlePaste = (event: ClipboardEvent) => {
    const image = Array.from(event.clipboardData.files).find((candidate) => candidate.type.startsWith('image/'))
    if (image) { event.preventDefault(); acceptFile(image); return }
    const text = event.clipboardData.getData('text/plain')
    if (isHttpUrl(text)) { event.preventDefault(); setUrl(text.trim()); setTab('url') }
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    acceptFile(Array.from(event.dataTransfer.files).find((candidate) => candidate.type.startsWith('image/')) ?? null)
  }

  return <ModalPortal><div className="modal-backdrop artwork-wizard-backdrop" onMouseDown={onClose} onPaste={handlePaste}>
    <section className="modal-card artwork-wizard" role="dialog" aria-modal="true" aria-labelledby="artwork-wizard-title" onMouseDown={(event) => event.stopPropagation()}>
      <header>
        <div><span className="eyebrow"><ImagePlus />{t('newMovies.artworkWizardEyebrow')}</span><h2 id="artwork-wizard-title">{t('newMovies.artworkWizardTitle')}</h2><p>{t('newMovies.artworkWizardHint')}</p></div>
        <button className="icon-button" type="button" onClick={onClose} aria-label={t('common.close')}><X /></button>
      </header>
      <nav className="artwork-wizard-tabs" aria-label={t('newMovies.artworkMethods')}>
        <button className={tab === 'search' ? 'active' : ''} type="button" onClick={() => setTab('search')}><Search />{t('newMovies.freeImageSearch')}</button>
        <button className={tab === 'paste' ? 'active' : ''} type="button" onClick={() => setTab('paste')}><ClipboardPaste />{t('newMovies.pasteOrUpload')}</button>
        <button className={tab === 'url' ? 'active' : ''} type="button" onClick={() => setTab('url')}><Link2 />{t('newMovies.imageAddress')}</button>
      </nav>

      <div className="artwork-wizard-body">
        {tab === 'search' && <section className="artwork-search-pane">
          <form className="artwork-search-form" onSubmit={(event) => { event.preventDefault(); void runSearch() }}>
            <Search /><input value={query} onChange={(event) => setQuery(event.target.value)} aria-label={t('newMovies.artworkSearchQuery')} />
            <button className="primary-button" type="submit" disabled={searching || query.trim().length < 2}>{searching ? <LoaderCircle className="spin" /> : <Search />}{t('newMovies.search')}</button>
          </form>
          {searching && <div className="artwork-wizard-state"><LoaderCircle className="spin" />{t('newMovies.searchingArtwork')}</div>}
          {!searching && <>
            {recommended && <section className="artwork-recommended">
              <header><div><strong>{t('newMovies.broadcasterSuggestion')}</strong><p>{t('newMovies.broadcasterSuggestionHint')}</p></div>
                {recommended.sourceUrl && <a href={recommended.sourceUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"><ExternalLink />{t('newMovies.openBroadcasterPage')}</a>}
              </header>
              <div className="artwork-result-grid artwork-recommended-grid"><ArtworkCandidate candidate={recommended} selected={selected?.id === recommended.id} onSelect={setSelected} untitled={t('newMovies.untitledArtwork')} licenseLabel={t('newMovies.broadcasterImage')} /></div>
            </section>}

            <section className="artwork-openverse">
              <div className="artwork-section-heading"><div><strong>{t('newMovies.openverseResults')}</strong><p className="artwork-source-note">{t('newMovies.openverseHint')}</p></div>
                <a className="quiet-button" href={webImageSearchUrl(query)} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"><ExternalLink />{t('newMovies.webImageSearch')}</a>
              </div>
              <p className="artwork-web-search-hint">{t('newMovies.webImageSearchHint')}</p>
              {searchError && <div className="inline-error">{searchError}</div>}
              {!searchError && results.length === 0 && <div className={`artwork-wizard-state ${recommended ? 'compact' : ''}`}><ImagePlus /><strong>{t('newMovies.noArtworkResults')}</strong><p>{t('newMovies.noArtworkResultsHint')}</p></div>}
              {results.length > 0 && <div className="artwork-result-grid">
                {results.map((candidate) => <ArtworkCandidate candidate={candidate} selected={selected?.id === candidate.id} key={candidate.id} onSelect={setSelected} untitled={t('newMovies.untitledArtwork')} licenseLabel={candidate.license || t('newMovies.licenseUnknown')} />)}
              </div>}
            </section>
          </>}
        </section>}

        {tab === 'paste' && <section className="artwork-paste-pane">
          <div className={`artwork-drop-zone ${filePreview ? 'has-preview' : ''}`} tabIndex={0} onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
            {filePreview ? <img src={filePreview} alt={t('newMovies.artworkPreview')} /> : <><ClipboardPaste /><strong>{t('newMovies.pasteImageHere')}</strong><p>{t('newMovies.pasteImageHint')}</p></>}
          </div>
          <div className="artwork-paste-actions">
            <button className="primary-button" type="button" onClick={() => void readClipboard()} disabled={saving}><ClipboardPaste />{t('newMovies.readClipboard')}</button>
            <button className="quiet-button" type="button" onClick={() => fileInput.current?.click()} disabled={saving}><FileUp />{t('newMovies.chooseFile')}</button>
            <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => { acceptFile(event.target.files?.[0] ?? null); event.currentTarget.value = '' }} />
          </div>
          <small>{t('newMovies.artworkFormats')}</small>
        </section>}

        {tab === 'url' && <section className="artwork-url-pane">
          <Link2 /><h3>{t('newMovies.imageAddress')}</h3><p>{t('newMovies.imageAddressHint')}</p>
          <label><span>{t('newMovies.artworkUrlLabel')}</span><input type="url" value={url} onChange={(event) => setUrl(event.target.value)} placeholder={t('newMovies.artworkUrlPlaceholder')} /></label>
          <small>{t('newMovies.imageAddressSafety')}</small>
        </section>}
      </div>

      {(actionError || externalError) && <div className="inline-error artwork-wizard-error">{actionError || externalError}</div>}
      <footer>
        {hasManualArtwork && onRemove && <button className="quiet-button danger" type="button" disabled={saving} onClick={() => void save(onRemove)}><Trash2 />{t('newMovies.removeManualArtwork')}</button>}
        <span />
        <button className="quiet-button" type="button" onClick={onClose}>{t('common.cancel')}</button>
        {tab === 'search' && <button className="primary-button" type="button" disabled={!selected || saving} onClick={() => selected && void save(() => onSelect(selected))}>{saving ? <LoaderCircle className="spin" /> : <Check />}{t('newMovies.useSelectedArtwork')}</button>}
        {tab === 'paste' && <button className="primary-button" type="button" disabled={!file || saving} onClick={() => file && void save(() => onUpload(file))}>{saving ? <LoaderCircle className="spin" /> : <Check />}{t('newMovies.useSelectedArtwork')}</button>}
        {tab === 'url' && <button className="primary-button" type="button" disabled={!isHttpUrl(url) || saving} onClick={() => void save(() => onSelect({ imageUrl: url.trim(), sourceUrl: url.trim() }))}>{saving ? <LoaderCircle className="spin" /> : <Check />}{t('newMovies.useArtworkUrl')}</button>}
      </footer>
    </section>
  </div></ModalPortal>
}

function isHttpUrl(value: string) {
  try { const parsed = new URL(value.trim()); return parsed.protocol === 'https:' || parsed.protocol === 'http:' } catch { return false }
}

function extensionFor(type: string) {
  if (type === 'image/png') return 'png'
  if (type === 'image/webp') return 'webp'
  return 'jpg'
}

function candidateFormat(candidate: ArtworkSearchCandidate) {
  if (!candidate.width || !candidate.height) return 'landscape'
  const ratio = candidate.width / candidate.height
  if (ratio < .86) return 'poster'
  if (ratio < 1.18) return 'square'
  return 'landscape'
}

function webImageSearchUrl(query: string) {
  return `https://duckduckgo.com/?q=${encodeURIComponent(`${query} Film Bild`)}&iax=images&ia=images`
}

function ArtworkCandidate({ candidate, selected, onSelect, untitled, licenseLabel }: {
  candidate: ArtworkSearchCandidate
  selected: boolean
  onSelect: (candidate: ArtworkSearchCandidate) => void
  untitled: string
  licenseLabel: string
}) {
  return <button type="button" className={selected ? 'selected' : ''} onClick={() => onSelect(candidate)}>
    <span className={`artwork-result-image ${candidateFormat(candidate)}`}><img src={candidate.thumbnailUrl} alt="" loading="lazy" />{selected && <b><Check /></b>}</span>
    <strong>{candidate.title || untitled}</strong>
    <small>{candidate.creator || candidate.sourceName}</small>
    <span>{licenseLabel}</span>
  </button>
}
